import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { PracticeChallenge } from "@/components/PracticeChallenge";
import { Insight } from "@/components/Insight";
import { Keyboard } from "@/components/Keyboard";
import {
  Tip, KeyIdea, Pull, Steps, Step, Vocab, Term, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation25() {
  const { lesson, course, accent } = findLesson("piano-foundation", "25");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>The same fingering works for almost every white-note major scale: <strong>1-2-3 · thumb-cross · 1-2-3-4-5</strong>. Three notes, tuck the thumb under, four more notes, end on the pinky.</Highlight>
        <Highlight>Six scales use this exact rule: <strong>A, B, C, D, E, G major</strong>. Same fingering. Different keys.</Highlight>
        <Highlight><strong>F major is the exception</strong> — because of the B♭, the thumb has to cross under the <em>ring finger</em>, not the middle. Use 1-2-3-4 then thumb-cross, 1-2-3-4.</Highlight>
        <Highlight>Cross <em>before</em> you play, not at the moment you need the next note. The thumb should already be on the next key while the previous finger is still pressed.</Highlight>
        <Highlight>Build speed in stages — <strong>crotchets</strong> (one note per beat), then <strong>quavers</strong> (two), then <strong>sixteenths</strong> (four). Don't rush — every speed has its own learning.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="The big idea" title="Three notes · cross · five notes" accent={accent.hex}>
        <p>
          A scale has more notes than your hand has fingers. The thumb-under technique
          (Lesson 23, the Pendulum) is what extends the hand. For a major scale ascending
          across one octave, the universal rule is:
        </p>

        <p className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-center font-display text-[15px] text-amber-200">
          <strong>1 - 2 - 3 — thumb crosses — 1 - 2 - 3 - 4 - 5</strong>
        </p>

        <KeyIdea>
          Why cross after the third? Because crossing after 1 or 2 is wasteful — too many
          crossings — and crossing after 4 or 5 is mechanically very tough. The middle finger
          is the natural pivot point for the thumb to slip under.
        </KeyIdea>

        <Vocab>
          <Term name="Ascending (Arohan)" def="going up the scale, low to high." />
          <Term name="Descending (Avarohan)" def="going down the scale, high to low." />
          <Term name="Crotchet" def="quarter note. One note per beat." />
          <Term name="Quaver" def="eighth note. Two notes per beat." />
        </Vocab>
      </Insight>

      <Insight eyebrow="The six matching scales" title="A, B, C, D, E, G major — all use the same shape" accent={accent.hex}>
        <p>
          Six of the seven white-note major scales use the exact same fingering ascending and
          descending. Different keys, different black notes underneath your fingers — but the
          1-2-3 / cross / 1-2-3-4-5 pattern doesn't change.
        </p>

        <div className="overflow-x-auto rounded-xl border border-nsm-border bg-nsm-card/50 p-4 text-[13px]">
          <table className="w-full">
            <thead>
              <tr className="text-nsm-dim">
                <th className="text-left">Scale</th>
                <th>Notes</th>
                <th>Sharps / flats</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              <tr className="border-t border-nsm-border"><td>A major</td><td>A B C♯ D E F♯ G♯</td><td>3♯</td></tr>
              <tr className="border-t border-nsm-border"><td>B major</td><td>B C♯ D♯ E F♯ G♯ A♯</td><td>5♯</td></tr>
              <tr className="border-t border-nsm-border"><td>C major</td><td>C D E F G A B</td><td>none</td></tr>
              <tr className="border-t border-nsm-border"><td>D major</td><td>D E F♯ G A B C♯</td><td>2♯</td></tr>
              <tr className="border-t border-nsm-border"><td>E major</td><td>E F♯ G♯ A B C♯ D♯</td><td>4♯</td></tr>
              <tr className="border-t border-nsm-border"><td>G major</td><td>G A B C D E F♯</td><td>1♯</td></tr>
            </tbody>
          </table>
        </div>

        <Pull>
          I think the fastest scale you'll ever play is B major. You can fly with B
          major.
        </Pull>
      </Insight>

      <Insight eyebrow="The crossing technique" title="Cross before you need to" accent={accent.hex}>
        <p>
          The most important rule for clean scales: <strong>your thumb should be over the
          next key while you're still playing the current one</strong>. Don't wait for the
          last millisecond.
        </p>

        <Pull>
          1, 2, 3, cross. See, I said cross but I didn't even play the note yet. So
          that's the secret.
        </Pull>

        <Steps>
          <Step>Play finger 1 (thumb).</Step>
          <Step>Play finger 2 (index).</Step>
          <Step>Play finger 3 (middle). At this exact moment, the thumb begins moving under.</Step>
          <Step>Thumb plays the next note — <em>and was already there</em> before finger 3 lifted.</Step>
          <Step>Continue with index, middle, ring, pinky. End on the octave.</Step>
        </Steps>

        <Tip>
          Coming down is the mirror image. Play the pinky on the top note, descend with
          ring, middle, index, thumb. Then the middle finger crosses <em>over</em> the thumb.
          Same logic; opposite direction.
        </Tip>
      </Insight>

      <Insight title="A major · the standard fingering visualised" accent={accent.hex}>
        <Keyboard
          startOctave={3}
          octaves={2}
          highlights={[
            { pitch: "A4", color: "#22c55e", label: "1" },
            { pitch: "B4", color: "#22c55e", label: "2" },
            { pitch: "C#5", color: "#22c55e", label: "3" },
            { pitch: "D5", color: "#ef4444", label: "1·cross" },
            { pitch: "E5", color: "#3b82f6", label: "2" },
            { pitch: "F#5", color: "#3b82f6", label: "3" },
            { pitch: "G#5", color: "#3b82f6", label: "4" },
            { pitch: "A5", color: "#3b82f6", label: "5" },
          ]}
          height={180}
        />

        <PracticeChallenge
          courseSlug="piano-foundation"
          order="25"
          id="amajor-scale"
          mode="sequence"
          title="A major scale · one octave"
          prompt="Play A major up: A B C♯ D E F♯ G♯ A. Tuck the thumb under after the third note."
          notes={["A4", "B4", "C#5", "D5", "E5", "F#5", "G#5", "A5"]}
          startOctave={4}
          octaves={2}
        />
        <p className="prose-nsm text-sm">
          <span className="text-emerald-300">Green</span> = first three fingers (1-2-3).{" "}
          <span className="text-rose-300">Red</span> = the moment the thumb tucks under.{" "}
          <span className="text-blue-300">Blue</span> = the four-finger walk to the octave.
        </p>
      </Insight>

      <Insight eyebrow="The exception · F major" title="Cross after the ring finger, not the middle" accent={accent.hex}>
        <p>
          F major has one flat — <strong>B♭</strong>. If you tried to apply the standard
          1-2-3-cross rule, the thumb would land on B♭ — and the elbow has to swing
          uncomfortably to make it work. The solution: cross under the
          <strong> ring finger</strong> instead.
        </p>

        <p className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3 text-center font-display text-[14px]">
          <strong>F major fingering: 1 - 2 - 3 - 4 — thumb-cross — 1 - 2 - 3 - 4</strong>
        </p>

        <ul className="space-y-1 text-[14px]">
          <li>F (thumb)</li>
          <li>G (index)</li>
          <li>A (middle)</li>
          <li>B♭ (ring) ← the cross point</li>
          <li>C (thumb crosses under)</li>
          <li>D (index), E (middle), F (ring)</li>
        </ul>

        <Tip>
          Pinky is unused in this scale across one octave. Coming down: F-E-D-C with
          fingers 4-3-2-1, then ring crosses over the thumb to play B♭, then A-G-F with
          3-2-1.
        </Tip>
      </Insight>

      <Insight eyebrow="The left hand" title="Pulse first · then toggle the octave" accent={accent.hex}>
        <p>
          The right hand is doing the scale. The left hand stays simple: hold the root note
          on the pulse — one hit per beat. As you grow comfortable, you can <strong>toggle
          between root and octave</strong> for richer LH movement.
        </p>

        <p>
          Three speed gears for the right-hand scale:
        </p>

        <ul className="space-y-1 text-[14px]">
          <li><strong>Crotchets</strong> — one scale note per beat. Slow and clean.</li>
          <li><strong>Quavers</strong> — two scale notes per beat. The standard scale speed.</li>
          <li><strong>Semiquavers</strong> — four notes per beat. Performance speed.</li>
        </ul>

        <Tip>
          When playing black keys, don't hit them at the very tip — push <em>slightly inside</em>{" "}
          the keyboard. The physics of the key are easier to handle when you're not at the
          edge. The thumb naturally stays at the edge because it's short — that's fine.
        </Tip>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-26"
        label="Lesson 26 · Scales Ascending & Descending - Black Note"
        why="Six white-note major scales done. The next lesson handles the five black-note majors — D♭, E♭, G♭, A♭, B♭ — with their own crossing patterns."
      />
    </LessonShell>
  );
}
